/**
 * MilestoneRow Component
 *
 * Displays a milestone in row format using BaseRow layout.
 * Shows due date, completion status, and linked tasks count.
 */

import React, { useMemo, useState } from 'react';
import { Flag, CheckCircle2, Circle } from 'lucide-react';
import { BaseRow } from '../common/base/BaseRow';
import { PrimaryAction } from '../common/base';
import { LinkManager } from '../../services/actions/LinkManager';
import { getAvailableActions } from '../common/registry/ActionRegistry';
import { useTranslation } from '../../hooks';
import type { Milestone } from '@business-assistant/shared/domain';

// =============================================================================
// TYPES
// =============================================================================

export interface MilestoneRowProps {
  entity: Milestone;
  onTitleClick?: (milestone: Milestone) => void;
  onActionClick?: (actionType: string, milestoneId: string) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MilestoneRow({ entity: milestone, onTitleClick, onActionClick }: MilestoneRowProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware date formatting
  const formatDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Primary action state (completion - milestone is completable)
  const [isCompleted, setIsCompleted] = useState(milestone.completed);

  // Get linked action counts for icon coloring
  const linkedCounts = useMemo(() => {
    return LinkManager.getLinkedActionCounts(milestone.id);
  }, [milestone.id]);

  // Left slot - Primary Action (check for completion)
  const leftSlot = (
    <PrimaryAction
      type="check"
      active={isCompleted}
      onChange={(completed) => {
        setIsCompleted(completed);
        onActionClick?.('complete', milestone.id);
      }}
    />
  );

  // Get available actions for milestones
  const availableActions = useMemo(() => {
    return getAvailableActions(milestone.id);
  }, [milestone.id]);

  // Build meta content
  const metaContent = useMemo(() => {
    const parts: React.ReactNode[] = [];

    // Due date with overdue indicator
    const isOverdue = !milestone.completed && new Date(milestone.dueDate) < new Date();
    parts.push(
      <span
        key="due"
        style={{
          ...styles.dueDate,
          ...(isOverdue ? styles.overdue : {}),
        }}
      >
        {isOverdue && '⚠️ '}
        {t('milestones.duePrefix')} {formatDate(milestone.dueDate)}
      </span>
    );

    // Linked tasks count
    if (milestone.linkedTaskIds && milestone.linkedTaskIds.length > 0) {
      const count = milestone.linkedTaskIds.length;
      parts.push(
        <span key="tasks" style={styles.tasksCount}>
          {t(count === 1 ? 'milestones.linkedTask' : 'milestones.linkedTasks_plural', { count })}
        </span>
      );
    }

    // Completion date
    if (milestone.completed && milestone.completedAt) {
      parts.push(
        <span key="completed" style={styles.completedDate}>
          {t('milestones.completedOn')} {formatDate(milestone.completedAt)}
        </span>
      );
    }

    // Involved users
    if (milestone.involvedUsers && milestone.involvedUsers.length > 0) {
      const count = milestone.involvedUsers.length;
      parts.push(
        <span key="users" style={styles.users}>
          {count} {count === 1 ? t('common.person') : t('common.people')}
        </span>
      );
    }

    return (
      <div style={styles.metaContainer}>
        {parts.map((part, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && <span style={styles.separator}>·</span>}
            {part}
          </React.Fragment>
        ))}
      </div>
    );
  }, [milestone, t, formatDate]);

  // Icon based on completion status
  const icon = useMemo(() => {
    if (milestone.completed) {
      return (
        <div style={styles.iconContainer}>
          <Flag size={14} style={{ color: '#16a34a' }} />
          <CheckCircle2 size={10} style={{ ...styles.completedBadge }} />
        </div>
      );
    }

    const isOverdue = new Date(milestone.dueDate) < new Date();
    return (
      <Flag
        size={16}
        style={{ color: isOverdue ? '#dc2626' : '#3b82f6' }}
      />
    );
  }, [milestone]);

  return (
    <BaseRow
      entity={{ ...milestone, title: milestone.title }}
      entityType="milestone"
      leftSlot={leftSlot}
      icon={icon}
      avatars={milestone.involvedUsers}
      metaContent={metaContent}
      availableActions={availableActions}
      linkedActionCounts={linkedCounts}
      onTitleClick={onTitleClick}
      onActionClick={onActionClick}
      done={isCompleted}
    />
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

// formatDate is now handled inside the component with locale awareness

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  metaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  separator: {
    color: '#ccc',
    margin: '0 2px',
  } as React.CSSProperties,
  dueDate: {
    color: '#666',
    fontWeight: 500,
  } as React.CSSProperties,
  overdue: {
    color: '#dc2626',
    fontWeight: 600,
  } as React.CSSProperties,
  tasksCount: {
    padding: '2px 6px',
    borderRadius: '3px',
    fontSize: '11px',
    background: '#dbeafe',
    color: '#1e40af',
  } as React.CSSProperties,
  completedDate: {
    color: '#16a34a',
    fontSize: '11px',
  } as React.CSSProperties,
  users: {
    color: '#999',
    fontSize: '11px',
  } as React.CSSProperties,
  iconContainer: {
    position: 'relative',
    display: 'inline-block',
  } as React.CSSProperties,
  completedBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-4px',
    background: '#fff',
    borderRadius: '50%',
    color: '#16a34a',
  } as React.CSSProperties,
};
